import { useController } from 'react-hook-form';

import {
  Alert,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@/ui';
import {
  COMPANY_PROVISIONING_PRESENTATION,
  getCompanyProvisioningStatus,
} from '@/utils';

import type { Company, CompanyForm } from '@/types';
import type { FC } from 'react';
import type { Control } from 'react-hook-form';

interface CompanyAccessProps {
  company: Pick<
    Company,
    | 'b2bOrganizationId'
    | 'b2bProvisioningMessage'
    | 'b2bProvisioningStatus'
    | 'b2b_organization_id'
    | 'contractedMembersCount'
    | 'selfPaidMembersCount'
  >;
  control: Control<CompanyForm>;
}
const COMMA_ONLY_HELPER_TEXT = 'Separate values with commas only.';
const COMMA_ONLY_ERROR = 'Use commas only, for example: repl.it, replit.com';
const NON_COMMA_SEPARATOR_PATTERN = /[\n\r;\t|]/u;
const AUTH_METHOD_HELPER_TEXT =
  'None allows OAuth or password. OAuth requires employees to use their company identity provider.';

const SELF_PAY_HELPER_TEXT =
  'Only the allow list is company-paid. Other domain members can still join, then pay by card.';
const CONTRACTED_SEATS_HELPER_TEXT =
  'Optional. The allow list cannot grow beyond this number.';

const commaOnlyRules = {
  validate: (value: string) =>
    NON_COMMA_SEPARATOR_PATTERN.test(value) ? COMMA_ONLY_ERROR : true,
};

const CompanyAccess: FC<CompanyAccessProps> = ({ company, control }) => {
  const provisioningStatus = getCompanyProvisioningStatus(company);
  const provisioningPresentation =
    COMPANY_PROVISIONING_PRESENTATION[provisioningStatus];
  const { field: domains, fieldState: domainState } = useController({
    control,
    name: 'domains',
  });
  const { field: emails, fieldState: emailState } = useController({
    control,
    name: 'emails',
    rules: commaOnlyRules,
  });
  const { field: adminEmails, fieldState: adminEmailState } = useController({
    control,
    name: 'adminEmails',
    rules: commaOnlyRules,
  });
  const { field: disallowedEmails, fieldState: disallowedEmailState } =
    useController({
      control,
      name: 'disallowedEmails',
      rules: commaOnlyRules,
    });
  const { field: requiredAuthMethod, fieldState: requiredAuthMethodState } =
    useController({
      control,
      name: 'required_auth_method',
    });
  const { field: domainMembersSelfPay } = useController({
    control,
    name: 'domainMembersSelfPay',
  });
  const { field: contractedSeats, fieldState: contractedSeatsState } =
    useController({
      control,
      name: 'contractedSeats',
    });
  const seatSummary =
    company.contractedMembersCount === undefined
      ? null
      : [
          `Contracted ${company.contractedMembersCount}${
            contractedSeats.value ? `/${contractedSeats.value}` : ''
          }`,
          `self-paid ${company.selfPaidMembersCount ?? 0}`,
        ].join(' · ');

  return (
    <Grid spacing={4} container>
      <Grid md={4} xs={12} item>
        <Typography className="mb-6" variant="h4">
          Employee access
        </Typography>
        <Typography className="mb-6" variant="body1">
          Anyone with the listed domain email will be able to access Mento. You
          can also only allow or block specific emails within the domain or
          outside the domain.
        </Typography>
      </Grid>
      <Grid className="mt-1 flex flex-col gap-3" md={8} xs={12} item>
        <TextField
          {...requiredAuthMethod}
          className="w-full"
          error={requiredAuthMethodState.invalid}
          helperText={
            requiredAuthMethodState.error?.message || AUTH_METHOD_HELPER_TEXT
          }
          label="Required Auth Method"
          variant="outlined"
          select
        >
          <MenuItem value="">None</MenuItem>
          <MenuItem value="oauth">OAuth</MenuItem>
        </TextField>
        <Alert severity={provisioningPresentation.severity}>
          <Typography variant="subtitle1">
            B2B provisioning: {provisioningStatus.replace('_', ' ')}
          </Typography>
          <Typography variant="body2">
            {company.b2bProvisioningMessage}
          </Typography>
          <Typography variant="body2">
            {provisioningPresentation.settingsAction}
          </Typography>
        </Alert>
        <TextField
          {...domains}
          className="w-full"
          error={domainState.invalid}
          helperText={domainState.error?.message || COMMA_ONLY_HELPER_TEXT}
          label="Domains"
          placeholder="repl.it, replit.com"
          rows={3}
          variant="outlined"
          multiline
        />
        <div className="flex flex-col gap-1">
          <FormControlLabel
            control={
              <Switch
                checked={!!domainMembersSelfPay.value}
                color="primary"
                onChange={(event) =>
                  domainMembersSelfPay.onChange(event.target.checked)
                }
              />
            }
            label="Domain members pay by card"
          />
          <Typography className="text-cement-700 ml-11 text-sm" variant="body2">
            {SELF_PAY_HELPER_TEXT}
          </Typography>
        </div>
        <TextField
          {...contractedSeats}
          className="w-full"
          error={contractedSeatsState.invalid}
          helperText={
            contractedSeatsState.error?.message || CONTRACTED_SEATS_HELPER_TEXT
          }
          label="Contracted seats"
          type="number"
          value={contractedSeats.value ?? ''}
          variant="outlined"
          onChange={(event) =>
            contractedSeats.onChange(
              event.target.value === '' ? null : Number(event.target.value),
            )
          }
        />
        {seatSummary && <Typography variant="body2">{seatSummary}</Typography>}
        <TextField
          {...emails}
          className="w-full"
          error={emailState.invalid}
          helperText={emailState.error?.message || COMMA_ONLY_HELPER_TEXT}
          label="Allow list of emails"
          placeholder="person@company.com, teammate@company.com"
          rows={3}
          variant="outlined"
          multiline
        />
        <TextField
          {...adminEmails}
          className="w-full"
          error={adminEmailState.invalid}
          helperText={adminEmailState.error?.message || COMMA_ONLY_HELPER_TEXT}
          label="Admin list of emails"
          placeholder="admin@company.com, ops@company.com"
          rows={3}
          variant="outlined"
          multiline
        />
        <TextField
          {...disallowedEmails}
          className="w-full"
          error={disallowedEmailState.invalid}
          helperText={
            disallowedEmailState.error?.message || COMMA_ONLY_HELPER_TEXT
          }
          label="disallow list of emails"
          placeholder="blocked@company.com, former@company.com"
          rows={3}
          variant="outlined"
          multiline
        />
      </Grid>
    </Grid>
  );
};
export default CompanyAccess;
